import { Slider } from '@vizit/atoms';
import { GridSingleIcon, GridSmallIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';

/** Images per row the slider allows, from the Actions Bar PRD: smallest thumbnails 10 per row, largest 3, default 7. */
const THUMBNAIL_COLUMNS = {
  min: 3,
  max: 10,
  default: 7,
} as const;

interface ThumbnailSizeSliderProps {
  /**
   * Images per row, between `THUMBNAIL_COLUMNS.min` and `THUMBNAIL_COLUMNS.max`.
   *
   * @required
   */
  columns: number;
  /**
   * Called with the new number of images per row.
   *
   * @required
   */
  onColumnsChange: (columns: number) => void;
  /**
   * Positions the control, e.g. fixed at the bottom of the page.
   *
   * @optional
   */
  className?: string;
}

/**
 * The slider value is minus the images per row, so moving right (higher value) gives fewer, bigger images, and
 * `signDisplay: 'never'` drops the minus for screen readers: they announce "Images per row, 7".
 */
const PER_ROW_FORMAT: Intl.NumberFormatOptions = { signDisplay: 'never' };

/**
 * Floating control that changes how big image thumbnails are in a grid, by setting how many images fit in a row.
 *
 * @example
 * const [columns, setColumns] = React.useState<number>(THUMBNAIL_COLUMNS.default);
 * <ThumbnailSizeSlider columns={columns} onColumnsChange={setColumns} className="fixed bottom-12 right-12" />
 */
function ThumbnailSizeSlider(props: ThumbnailSizeSliderProps) {
  const { columns, onColumnsChange, className } = props;

  return (
    <div className={cx('flex h-24 w-[320px] items-center gap-6 bg-white px-8 shadow-lg', className)}>
      <GridSmallIcon size={20} aria-hidden className="shrink-0 text-quaternary" />
      <Slider
        aria-label="Images per row"
        minValue={-THUMBNAIL_COLUMNS.max}
        maxValue={-THUMBNAIL_COLUMNS.min}
        value={-columns}
        formatOptions={PER_ROW_FORMAT}
        onChange={(value) => onColumnsChange(-value)}
      />
      <GridSingleIcon size={20} aria-hidden className="shrink-0 text-quaternary" />
    </div>
  );
}

export { THUMBNAIL_COLUMNS, ThumbnailSizeSlider };
export type { ThumbnailSizeSliderProps };
